import React, { ReactNode, RefObject, useContext } from 'react';
import { Dimensions, Platform, StyleSheet } from 'react-native';
import Animated, {
  useAnimatedGestureHandler,
  useAnimatedStyle,
  useAnimatedReaction,
  withSpring,
  scrollTo,
  withTiming,
  useSharedValue,
  runOnJS,
} from 'react-native-reanimated';
import {
  GestureEvent,
  GestureHandlerRootView,
  PanGestureHandler,
  PanGestureHandlerGestureEvent,
  TapGestureHandler,
  TapGestureHandlerEventPayload,
} from 'react-native-gesture-handler';

import { ConfigContext, animationConfig, Positions } from './ConfigContext';

interface ItemProps {
  children: ReactNode;
  positions: Animated.SharedValue<Positions>;
  id: string;
  token:string;
  editing: boolean;
  onDragEnd: (diffs: Positions) => void;
  scrollView: RefObject<Animated.ScrollView>;
  scrollY: Animated.SharedValue<number>;
  scrollHeight: Animated.SharedValue<number>;
  onPressEnd: (key: string) => void;
  onPress?: (x : number, y: number, token: string) => void;
}

const Item = ({
  children,
  positions,
  id,
  token,
  onDragEnd,
  scrollView,
  scrollY,
  scrollHeight,
  editing,
  onPressEnd,
  onPress,
}: ItemProps) => {
  const HEIGHT = 30;
  const config = useContext(ConfigContext);
  const { SIZE, COL, getOrder, getPosition } = config;
  const containerHeight = Dimensions.get('window').height - 40;
  const contentHeight = (Object.keys(positions.value).length / COL) * HEIGHT;
  const isGestureActive = useSharedValue(false);

  const position = getPosition(config, positions.value[id]!);
  const translateX = useSharedValue(position.x);
  const translateY = useSharedValue(position.y);
  
  useAnimatedReaction(
    () => positions.value[id]!,
    (newOrder) => {
      if (!isGestureActive.value) {
        const pos = getPosition(config, newOrder);
        translateX.value = withTiming(pos.x, animationConfig);
        translateY.value = withTiming(pos.y, animationConfig);
      }
    }
  );

  const eventHandler = useAnimatedGestureHandler<GestureEvent, {  }>({  
    //GestureEvent<TapGestureHandlerEventPayload>
    onStart: (event, ctx) => {  //console.log("pressed");  
                              },  
    onEnd: (event, ctx) => {    //console.log("pressed end");  
                                runOnJS(onPressEnd)(token); },});

  let bIgnore = false;

  const onGestureEvent = useAnimatedGestureHandler<
    PanGestureHandlerGestureEvent,
    { x: number; y: number }
  >({

    onStart: (_, ctx) => {
      // dont allow drag start if we're done editing
      if (onPress)
        //onPress(_.absoluteX, _.absoluteY, token);
         runOnJS(onPress)(_.absoluteX, _.absoluteY, token);

      if (editing) {
        //console.log("start " + _.absoluteX);

        // if (_.absoluteX >= 225 && Platform.OS == "android") //233)
        //   {
        //   console.log("ignoring");
        //   bIgnore = true;
        //   return;
        //   }
        // else
          bIgnore = false;

        //console.log(_.absoluteX + "." + _.absoluteY);

        ctx.x = translateX.value;
        ctx.y = translateY.value;
        isGestureActive.value = true;
      }
    },
    onActive: ({ translationX, translationY }, ctx) => {
       //if (onPress)
        // runOnJS(onPress)(translationX, translationY);

      // dont allow drag if we're done editing
      if (editing) {
        //console.log("active: " + ctx.x);

       if (bIgnore == true)
         return;

        translateX.value = ctx.x + translationX;
        translateY.value = ctx.y + translationY;
        // 1. We calculate where the tile should be
        const newOrder = getOrder(
          config,
          translateX.value,
          translateY.value,
          Object.keys(positions.value).length - 1
        );

        // 2. We swap the positions
        const oldOlder = positions.value[id];
        if (newOrder !== oldOlder) {
          const idToSwap = Object.keys(positions.value).find(
            (key) => positions.value[key] === newOrder
          );
          if (idToSwap) {
            // Spread operator is not supported in worklets
            // And Object.assign doesn't seem to be working on alpha.6
            const newPositions = JSON.parse(JSON.stringify(positions.value));
            newPositions[id] = newOrder;
            newPositions[idToSwap] = oldOlder;
            positions.value = newPositions;
          }
        }

        // 3. Scroll up and down if necessary
        const lowerBound = scrollY.value;
        const upperBound = lowerBound + scrollHeight.value - HEIGHT;
        const maxScroll = contentHeight - scrollHeight.value;
        const leftToScrollDown = maxScroll - scrollY.value;
        if (translateY.value < lowerBound) {
          const diff = Math.min(lowerBound - translateY.value, lowerBound);
          scrollY.value -= diff;
          scrollTo(scrollView, 0, scrollY.value, false);
          ctx.y -= diff;
          translateY.value = ctx.y + translationY;
        }
        if (translateY.value > upperBound) {
          const diff = Math.min(
            translateY.value - upperBound,
            leftToScrollDown
          );
          scrollY.value += diff;
          scrollTo(scrollView, 0, scrollY.value, false);
          ctx.y += diff;
          translateY.value = ctx.y + translationY;
        }
      }
    },
    onEnd: () => {

      const newPosition = getPosition(config, positions.value[id]!);
      translateX.value = withTiming(newPosition.x, animationConfig, () => {
        isGestureActive.value = false;
        runOnJS(onDragEnd)(positions.value);
      });
      translateY.value = withTiming(newPosition.y, animationConfig);
    },

  });
  const style = useAnimatedStyle(() => {
    const zIndex = isGestureActive.value ? 100 : 0;
    const scale = withSpring(isGestureActive.value ? 1.05 : 1);
    return {
      position: 'absolute',
      top: 0,
      left: 0,
      width: SIZE + 180,
      height: HEIGHT,
      zIndex,
      transform: [
        { translateX: translateX.value },
        { translateY: translateY.value },
        { scale },
      ],
    };
  });
  return (
    <Animated.View style={style}>
      {/* <TapGestureHandler onGestureEvent={eventHandler}> */}
      <GestureHandlerRootView style={{ flex: 1 }}>
      <PanGestureHandler enabled={editing} onGestureEvent={onGestureEvent}>
        <Animated.View style={StyleSheet.absoluteFill}>
          {children}
        </Animated.View>
      </PanGestureHandler>
      </GestureHandlerRootView>
      {/* </TapGestureHandler> */}
    </Animated.View>
  );
};

export default Item;
